import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import useIsMobile from '../../lib/useIsMobile';
import useSEO from '../../lib/useSEO';
import MarketingNav from './MarketingNav';
import Footer from './Footer';

const C = {
  bg: '#FAFBFC',
  white: '#FFFFFF',
  navy: '#1B2B4B',
  accent: '#2563EB',
  green: '#16A34A',
  muted: '#64748B',
  light: '#94A3B8',
  border: '#E2E8F0',
};

const baseFaqItems = [
  {
    question: 'What is a cost segregation study?',
    answer:
      'An IRS-recognized tax strategy that identifies components of your rental property \u2014 flooring, cabinetry, plumbing, electrical, landscaping \u2014 that can be depreciated faster than the standard 27.5-year schedule. By reclassifying these assets into 5, 7, or 15-year recovery periods, you reduce your income taxes now instead of over three decades.',
  },
  {
    question: 'How does this save me on my income taxes?',
    answer:
      'Depreciation is a deduction on your federal income tax return \u2014 not your property tax bill. Cost segregation increases your deductions in the current tax year, which reduces your taxable income. For most rental property owners, this means $30,000\u2013$75,000+ in additional first-year deductions.',
  },
  {
    question: 'Who qualifies for cost segregation?',
    answer:
      'Any owner of a single-family rental property used for investment or business purposes. The property must be placed in service (available for rent). It doesn\'t matter if you bought it last month or ten years ago \u2014 both qualify.',
  },
  {
    question: 'I\'m a high W-2 earner. Can cost segregation offset my W-2 income?',
    answer:
      'If you or your spouse qualify as a Real Estate Professional (REPS) under IRS rules, or if your AGI is below certain thresholds, the depreciation can offset your W-2 income. Even without REPS status, the deductions offset your rental income and carry forward. Talk to your CPA about your specific situation.',
  },
  {
    question: 'How much does a study cost?',
    answer: (studyFee) =>
      `$${studyFee} for a single-family rental property. Includes the full engineering analysis, PE certification, and delivery to your CPA. The study pays for itself many times over in Year 1.`,
  },
  {
    question: 'I bought my property years ago. Is it too late?',
    answer:
      'No. The IRS allows a \u201clook-back\u201d study on properties placed in service in prior years. You claim all the missed accelerated depreciation as a one-time catch-up deduction using Form 3115. No amended returns needed.',
  },
  {
    question: 'How long does the process take?',
    answer:
      'Most studies are completed within 2 weeks. You submit your details online, we build the study, a licensed PE certifies it, and we deliver it to your CPA.',
  },
  {
    question: 'Do I need a physical inspection?',
    answer: (studyFee, engFee) =>
      `No. We use property data, photos, construction records, and county assessor information to build your study. If you'd prefer an on-site visit, we offer a ClearSeg Engineer visit for $${engFee} \u2014 a licensed professional photographs and documents everything for you.`,
  },
  {
    question: 'Will this trigger an IRS audit?',
    answer:
      'No. Cost segregation is IRS-recognized and, when performed correctly, fully compliant with tax regulations. Our studies follow the IRS Cost Segregation Audit Techniques Guide. Every study is PE-certified, which adds credibility and defensibility.',
  },
  {
    question: 'I\'m planning to sell my property soon. Does it still make sense?',
    answer:
      'Even with a short ownership period, cost segregation can provide significant tax savings. However, depreciation recapture (IRC Sections 1245 and 1250) applies at sale. Our team can help you evaluate whether the near-term savings outweigh the recapture.',
  },
  {
    question: 'What documents do I need?',
    answer:
      'At minimum: purchase price and property address. Helpful but not required: closing disclosure, appraisal, tax assessment, and interior/exterior photos. Don\'t have everything? We can work with what you have.',
  },
  {
    question: 'What\'s the difference between cost segregation and regular depreciation?',
    answer:
      'Standard depreciation spreads your entire building cost (minus land) evenly over 27.5 years. Cost segregation identifies components that qualify for 5, 7, or 15-year depreciation \u2014 appliances, flooring, landscaping, specialized electrical \u2014 giving you much larger deductions in the early years, when the money is most valuable.',
  },
];

function AccordionItem({ item, isOpen, onToggle, isMobile }) {
  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${C.border}`,
        borderLeft: isOpen ? `3px solid ${C.green}` : `1px solid ${C.border}`,
        borderRadius: 8,
        marginBottom: 12,
        overflow: 'hidden',
        transition: 'border-left 0.2s ease',
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          padding: isMobile ? '16px 18px' : '20px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            color: C.navy,
            fontSize: isMobile ? 15 : 17,
            fontWeight: 600,
            lineHeight: 1.4,
            paddingRight: 16,
          }}
        >
          {item.question}
        </span>
        <span
          style={{
            color: C.light,
            fontSize: 22,
            flexShrink: 0,
            transition: 'transform 0.3s ease',
            transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
            lineHeight: 1,
          }}
        >
          +
        </span>
      </button>
      <div
        style={{
          maxHeight: isOpen ? 300 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.35s ease, padding 0.35s ease',
          padding: isOpen ? (isMobile ? '0 18px 16px 18px' : '0 24px 20px 24px') : (isMobile ? '0 18px 0 18px' : '0 24px 0 24px'),
        }}
      >
        <p
          style={{
            color: C.muted,
            fontSize: isMobile ? 14 : 15,
            lineHeight: 1.7,
            margin: 0,
          }}
        >
          {item.answer}
        </p>
      </div>
    </div>
  );
}

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState(null);
  const [studyFee, setStudyFee] = useState('799');
  const [engFee, setEngFee] = useState('199');
  const isMobile = useIsMobile();
  useSEO({ title: 'Cost Segregation FAQ | ClearSeg', description: 'Frequently asked questions about cost segregation studies for rental properties. Learn about savings, process, IRS compliance.', canonical: 'https://clearseg.com/faq' });

  useEffect(() => {
    api.get('/config/fees').then(res => {
      if (res.data?.single_family) setStudyFee(Number(res.data.single_family).toLocaleString());
    }).catch(() => {});
    api.get('/config/engineer-visit-fee').then(res => {
      if (res.data?.fee) setEngFee(Number(res.data.fee).toLocaleString());
    }).catch(() => {});
  }, []);

  const faqItems = useMemo(() =>
    baseFaqItems.map(item => ({
      ...item,
      answer: typeof item.answer === 'function' ? item.answer(studyFee, engFee) : item.answer,
    })),
  [studyFee, engFee]);

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    };
    let script = document.getElementById('faq-schema');
    if (!script) {
      script = document.createElement('script');
      script.id = 'faq-schema';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, [faqItems]);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div style={{ background: C.bg, minHeight: '100vh' }}>
      <MarketingNav />

      <section
        style={{
          textAlign: 'center',
          padding: isMobile ? '60px 20px 40px' : '100px 24px 60px',
          maxWidth: 800,
          margin: '0 auto',
        }}
      >
        <h1
          style={{
            color: C.navy,
            fontSize: isMobile ? 28 : 44,
            fontWeight: 700,
            margin: '0 0 16px',
            lineHeight: 1.2,
          }}
        >
          Frequently Asked Questions
        </h1>
        <p
          style={{
            color: C.muted,
            fontSize: isMobile ? 15 : 18,
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          Everything you need to know about cost segregation for your rental
          property.
        </p>
      </section>

      <section
        style={{
          maxWidth: 800,
          margin: '0 auto',
          padding: isMobile ? '0 20px 60px' : '0 24px 80px',
        }}
      >
        {faqItems.map((item, index) => (
          <AccordionItem
            key={index}
            item={item}
            isOpen={openIndex === index}
            onToggle={() => handleToggle(index)}
            isMobile={isMobile}
          />
        ))}
      </section>

      <section
        style={{
          textAlign: 'center',
          padding: isMobile ? '40px 20px 60px' : '60px 24px 100px',
          maxWidth: 600,
          margin: '0 auto',
        }}
      >
        <h2
          style={{
            color: C.navy,
            fontSize: isMobile ? 22 : 28,
            fontWeight: 700,
            margin: '0 0 16px',
          }}
        >
          Still have questions?
        </h2>
        <p
          style={{
            color: C.muted,
            fontSize: isMobile ? 14 : 16,
            lineHeight: 1.6,
            margin: '0 0 32px',
          }}
        >
          We're happy to help. Email us at{' '}
          <a href="mailto:info@clearseg.com" style={{ color: C.accent, textDecoration: 'none' }}>info@clearseg.com</a>{' '}
          or get started with your study today.
        </p>
        <Link
          to="/intake"
          style={{
            display: 'inline-block',
            background: C.navy,
            color: '#FFFFFF',
            fontSize: isMobile ? 15 : 16,
            fontWeight: 600,
            padding: isMobile ? '12px 28px' : '14px 36px',
            borderRadius: 8,
            textDecoration: 'none',
            transition: 'opacity 0.2s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = 0.9)}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = 1)}
        >
          Start Your Study
        </Link>
      </section>

      <Footer />
    </div>
  );
}
